import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { removeMember as removeMemberApi } from "../api/members";
import { useHostToast } from "../../host/useHostToast";
import { useTrack } from "../../host/useTrack";

const MY_TEAM = "My Team";

interface UseRemoveMember {
  pending: boolean;
  remove: (memberId: string | number) => Promise<boolean>;
}

// Tracking events mirror RemoveTeamMember.vue:
//   - success: "Team management > Remove member from team > Removed successfully"
//   - failure: "Team management > Remove member from team > Remove failed"
export function useRemoveMember(): UseRemoveMember {
  const { t } = useTranslation();
  const toast = useHostToast();
  const track = useTrack();
  const [pending, setPending] = useState(false);

  const remove = useCallback(
    async (memberId: string | number) => {
      setPending(true);
      try {
        await removeMemberApi(memberId);
        track("Team management > Remove member from team > Removed successfully", {
          eventCategory: MY_TEAM,
        });
        toast.success(t("team.toast.memberRemoved"));
        return true;
      } catch {
        track("Team management > Remove member from team > Remove failed", {
          eventCategory: MY_TEAM,
        });
        toast.error(t("team.toast.memberRemoveFailed"));
        return false;
      } finally {
        setPending(false);
      }
    },
    [t, toast, track],
  );

  return { remove, pending };
}
